import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CardHeader, Container, Grid, MenuItem, TextField, Typography } from "@mui/material";
import MaterialTable from "material-table";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

import { Icons } from "../utils/Icon";
import { CustomStyles } from "../utils/Styled";
import { useAuth } from "../hooks/useAuth";
import { GetTokenIdentity } from "../utils/Token";

const customerTypeOptions = ["All", "End User", "Dealer"];

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatInvoiceDate = (value) => {
    if (!value) return "-";

    const parsed = moment(value);
    if (!parsed.isValid()) return value;

    return parsed.format("DD MMM YYYY");
};

const mapCustomerTypeToSegment = (value) => {
    if (value === "End User") return "end_users";
    if (value === "Dealer") return "dealers";
    return "all";
};

const parseMonthYear = (monthYearLabel) => {
    const parsed = moment(monthYearLabel, ["MMMM YYYY", "MMM YYYY"], true);
    if (parsed.isValid()) {
        return {
            month: parsed.month() + 1,
            year: parsed.year(),
        };
    }

    return {
        month: moment().month() + 1,
        year: moment().year(),
    };
};

export const RepurchaseCustomersListPage = () => {
    const classes = CustomStyles;
    const navigate = useNavigate();
    const { state } = useLocation();
    const { token, logout } = useAuth();
    const identity = GetTokenIdentity(token);

    const monthYear = state?.monthYear || moment().format("MMMM YYYY");
    const fallbackPeriod = parseMonthYear(monthYear);
    const month = Number(state?.month) || fallbackPeriod.month;
    const year = Number(state?.year) || fallbackPeriod.year;
    const countryId = Number(state?.countryId) || Number(identity?.user?.country_id);

    const [searchKeyword, setSearchKeyword] = useState("");
    const [customerType, setCustomerType] = useState("All");
    const searchInputRef = useRef(null);

    const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
    const [appliedCustomerType, setAppliedCustomerType] = useState("All");
    const [data, setData] = useState([]);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [count, setCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState({
        totalCount: Number(state?.totalRepurchased) || 0,
        endUsersCount: Number(state?.endUsers) || 0,
        dealersCount: Number(state?.dealers) || 0,
    });

    const fetchRepurchaseCustomers = useCallback(async () => {
        if (!countryId) {
            setData([]);
            setCount(0);
            return;
        }

        setIsLoading(true);
        try {
            const params = {
                month,
                year,
                segment: mapCustomerTypeToSegment(appliedCustomerType),
                page: page + 1,
                limit: perPage,
            };

            if (appliedSearchKeyword.trim()) {
                params.search = appliedSearchKeyword.trim();
            }

            const response = await axios.get(process.env.REACT_APP_WEBSITE_BACKEND_URL + `api/v1/customer-dashboard/${countryId}/repurchase-customers`, {
                params,
            });

            const payload = response?.data?.data || {};
            const rows = (payload.data || []).map((item) => ({
                code: item.customerCode,
                company_name: item.customerName,
                email: item.email,
                customer_type: item.customerType,
                invoice_date: formatInvoiceDate(item.invoiceDate),
                sales: Number(item.sales || 0),
            }));

            setData(rows);
            setCount(Number(payload.total || 0));
            setSummary({
                totalCount: Number(payload?.summary?.totalCount || payload.total || 0),
                endUsersCount: Number(payload?.summary?.endUsersCount || 0),
                dealersCount: Number(payload?.summary?.dealersCount || 0),
            });
        } catch (error) {
            if (error?.response?.status === 401) {
                logout("Your account has been logged out of a session.");
                return;
            }

            const message = error?.response?.data?.message || "Unable to fetch repurchase customer list.";
            Swal.fire({
                icon: "error",
                title: "Failed to Load Data",
                text: message,
                confirmButtonColor: "#fe7320",
            });
        } finally {
            setIsLoading(false);
        }
    }, [countryId, month, year, appliedCustomerType, page, perPage, appliedSearchKeyword, logout]);

    useEffect(() => {
        fetchRepurchaseCustomers();
    }, [fetchRepurchaseCustomers]);

    const columns = [
        {
            title: "Code",
            field: "code",
            cellStyle: { width: 130, maxWidth: 130 },
            headerStyle: { width: 130, maxWidth: 130 },
        },
        {
            title: "Company Name",
            field: "company_name",
        },
        {
            title: "Email",
            field: "email",
        },
        {
            title: "Customer Type",
            field: "customer_type",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Invoice Date",
            field: "invoice_date",
            cellStyle: { width: 150, maxWidth: 150 },
            headerStyle: { width: 150, maxWidth: 150 },
        },
        {
            title: "Sales",
            field: "sales",
            render: (rowData) => formatCurrency(rowData.sales),
            cellStyle: { width: 120, maxWidth: 120 },
            headerStyle: { width: 120, maxWidth: 120 },
        },
    ];

    return (
        <div>
            <div className={classes.background} />
            <Container maxWidth="xl" sx={{ py: 2 }} style={{ padding: 14 }}>
                <MaterialTable
                    title={`Repurchase Customers List: ${monthYear}`}
                    icons={Icons}
                    columns={columns}
                    data={data}
                    page={page}
                    totalCount={count}
                    isLoading={isLoading}
                    style={{ padding: "24px" }}
                    options={{
                        pageSize: perPage,
                        pageSizeOptions: [10, 20, 30, 50],
                        search: false,
                        filtering: false,
                        sorting: false,
                        actionsColumnIndex: -1,
                        headerStyle: {
                            fontWeight: "bold",
                            backgroundColor: "#f1f1f1",
                            color: "#212122",
                        },
                        maxBodyHeight: "600px",
                        paginationType: "stepped",
                        padding: "dense",
                        draggable: false,
                        rowStyle: () => ({ backgroundColor: "#ffffff" }),
                    }}
                    localization={{
                        pagination: {
                            labelRowsPerPage: "",
                            labelDisplayedRows: "{from}-{to} of {count}",
                            labelRowsSelect: "rows",
                        },
                        toolbar: {
                            nRowsSelected: "{0} row(s) selected",
                        },
                        header: {
                            actions: "Actions",
                        },
                        body: {
                            emptyDataSourceMessage: "No data to display",
                        },
                    }}
                    onChangePage={(nextPage) => setPage(nextPage)}
                    onChangeRowsPerPage={(nextPerPage) => {
                        setPerPage(nextPerPage);
                        setPage(0);
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <Grid container spacing={2} sx={{ px: 0.1, pb: 1.5 }}>
                                    <Grid item xs={12} sm={8}>
                                        <CardHeader
                                            sx={{ p: 0 }}
                                            title={
                                                <Typography component="h1" variant="h5" color="primary" fontWeight="bold">
                                                    Repurchase Customers List: {monthYear}
                                                </Typography>
                                            }
                                            subheader={
                                                <Typography sx={{ mt: 0.8, color: "#2e3d4f", fontSize: "1rem" }}>
                                                    Total Repurchased: {summary.totalCount} Accounts ({summary.endUsersCount} End Users | {summary.dealersCount} Dealers)
                                                </Typography>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                                        <Button color="error" onClick={() => navigate(-1)}>
                                            Back
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mt: 1.2, pb: 2 }}>
                                    <Box sx={{ flex: "1 1 320px", minWidth: 260, maxWidth: 420 }}>
                                        <TextField
                                            autoComplete="off"
                                            fullWidth
                                            size="small"
                                            label="Search by Name/Code"
                                            autoFocus={searchInputRef.current === document.activeElement}
                                            inputRef={searchInputRef}
                                            value={searchKeyword}
                                            onChange={(event) => setSearchKeyword(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: "0 0 220px", minWidth: 220 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="Customer Type"
                                            value={customerType}
                                            onChange={(event) => setCustomerType(event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            {customerTypeOptions.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: "0 0 130px", minWidth: 130 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            sx={{ height: "40px", whiteSpace: "nowrap" }}
                                            onClick={() => {
                                                setAppliedSearchKeyword(searchKeyword);
                                                setAppliedCustomerType(customerType);
                                                setPage(0);
                                            }}
                                            disabled={isLoading || !countryId}
                                        >
                                            Show
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        ),
                    }}
                />
            </Container>
        </div>
    );
};
